import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'

import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { readMemberResource, writeMemberResource } from '../lib/resourceCache'
import StableFacilityImage from '../components/StableFacilityImage'
import { normalizeMemberFacilities } from '../lib/normalizeMemberFacilities'

export default function MemberFacilitiesPage({ clubCode }) {
  const { user } = useAuth0()
  return <ScopedFacilitiesPage key={JSON.stringify([clubCode, user?.sub])} clubCode={clubCode} />
}

function ScopedFacilitiesPage({ clubCode }) {
  const { t: memberText } = useOptionalPrivanaI18n()

  const { user } = useAuth0()
  const cacheScope = React.useMemo(() => ({ clubCode, memberIdentity: user?.sub }), [clubCode, user?.sub])
  const cached = React.useMemo(() => readMemberResource(cacheScope, 'facilities', { ttlMs: 30 * 60 * 1000 }), [cacheScope])
  const [facilities, setFacilities] = useState(() => normalizeMemberFacilities(cached?.stale ? null : cached?.data))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryTick, setRetryTick] = useState(0)
  const { memberPortalApiGet } = useMemberPortalApi()
  const navigate = useNavigate()

  useEffect(() => { setFacilities(normalizeMemberFacilities(cached?.stale ? null : cached?.data)); setError(null) }, [cached, cacheScope])

  useEffect(() => {
    let cancelled = false

    async function loadFacilities() {
      if (!clubCode || !user?.sub) return
      setLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/facilities`)
        const json = await res.json().catch(() => null)

        if (!res.ok) {
          const message =
            json?.error && json?.reason
              ? `${json.error} / ${json.reason}`
              : json?.error || 'failed_to_fetch_facilities'
          throw new Error(message)
        }

        if (!cancelled) {
          const next = normalizeMemberFacilities(json?.data)
          setFacilities(next)
          writeMemberResource(cacheScope, 'facilities', next)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load member facilities', err)
          setError(err?.message || 'Unable to load facilities right now.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadFacilities()

    return () => {
      cancelled = true
    }
  }, [cacheScope, clubCode, memberPortalApiGet, retryTick])

  const handleFacilityClick = (facility) => {
    const key = facility?.facility_key || facility?.facilityKey
    const facilityRoutes = {
      golf: `/${clubCode}/golf`,
      tennis: `/${clubCode}/book-racquet/tennis`,
      squash: `/${clubCode}/book-racquet/squash`,
      pickleball: `/${clubCode}/book-racquet/pickleball`,
      padel: `/${clubCode}/book-racquet/padel`,
      spa: `/${clubCode}/spa`,
      fitness_classes: `/${clubCode}/fitness`,
    }

    const target = facilityRoutes[key]
    if (target) {
      navigate(target)
    }
  }

  return (
    <div className="mp-panel">
      <div className="mp-panel__header">
        <div>
          <p className="mp-eyebrow">{memberText("portalPresentation.member_book_facilities")}</p>
          <h4 className="mp-panel__title">{memberText("portalPresentation.member_choose_your_next_activity")}</h4>
        </div>
      </div>

      {error ? <div className="mp-warning">{memberText("portalPresentation.member_facilities_could_not_be_refreshed")} <button className="mp-link" type="button" onClick={() => setRetryTick(value => value + 1)}>{memberText("portalPresentation.member_try_again")}</button></div> : null}

      {loading && facilities.length === 0 ? (
        <div className="mp-facility-grid" aria-label={memberText("portalPresentation.member_loading_facilities")}>{[1,2,3,4].map(item => <div key={item} className="mp-facility-card mp-resource-skeleton" aria-hidden="true" />)}</div>
      ) : facilities.length === 0 ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_facilities_will_appear_here_when_your_club_enables_them")}</p>
      ) : (
        <div className="mp-facility-grid">
          {facilities.map((facility) => {
            const key = facility?.facility_key || facility?.facilityKey
            const facilityRoutes = {
              golf: `/${clubCode}/golf`,
              tennis: `/${clubCode}/book-racquet/tennis`,
              squash: `/${clubCode}/book-racquet/squash`,
              pickleball: `/${clubCode}/book-racquet/pickleball`,
              padel: `/${clubCode}/book-racquet/padel`,
              spa: `/${clubCode}/spa`,
              fitness_classes: `/${clubCode}/fitness`,
            }
            const facilityHints = {
              golf: "portalPresentation.member_book_tee_times",
              tennis: "portalPresentation.member_reserve_courts",
              squash: "portalPresentation.member_reserve_courts",
              pickleball: "portalPresentation.member_reserve_courts",
              padel: "portalPresentation.member_reserve_courts",
              spa: "portalPresentation.member_book_spa_services",
              fitness_classes: "portalPresentation.member_book_fitness_classes",
            }
            const isNavigable = Boolean(facilityRoutes[key])
            return (
              <div
                key={facility.facility_key || facility.id}
                className={`mp-facility-card ${isNavigable ? 'is-clickable' : ''}`}
                role={isNavigable ? 'button' : undefined}
                tabIndex={isNavigable ? 0 : -1}
                onClick={isNavigable ? () => handleFacilityClick(facility) : undefined}
                onKeyDown={
                  isNavigable
                    ? (evt) => {
                        if (evt.key === 'Enter' || evt.key === ' ') {
                          evt.preventDefault()
                          handleFacilityClick(facility)
                        }
                      }
                    : undefined
                }
              >
              <StableFacilityImage url={facility.image_url} identity={facility.image_identity || facility.image_url} unavailable={facility.image_unavailable === true} />
              <div className="mp-facility-card__content">
                <div>
                  <div className="mp-facility-card__name">{facility.display_name_key ? memberText(facility.display_name_key) : facility.display_name}</div>
                  <div className="mp-facility-card__hint">
                    {memberText(facilityHints[key] || "portalPresentation.member_booking_coming_soon")}
                  </div>
                </div>
              </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
